import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCourseAccess } from './useCourseAccess';
import { LiveSessionService } from '../services/liveSessionService';
import { Question, QuestionFormData } from '../types/liveSessions';

export const useLiveSessions = (sessionId: number | null) => {
  const { user } = useAuth();
  const { getAvailableLiveSessions } = useCourseAccess();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const availableSessions = getAvailableLiveSessions();

  useEffect(() => {
    if (!sessionId) return;

    const unsubscribe = LiveSessionService.subscribeToQuestions(sessionId, (questions) => {
      setQuestions(questions);
    });

    return () => unsubscribe();
  }, [sessionId]);

  const submitQuestion = async (questionData: QuestionFormData) => {
    if (!sessionId || !user) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await LiveSessionService.addQuestion(sessionId, user.id, user.name || 'Anonymous', questionData);
      return true;
    } catch (err) {
      setError('Failed to submit question. Please try again.');
      console.error('Error submitting question:', err);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const upvoteQuestion = async (questionId: string) => {
    if (!user) return;

    try {
      await LiveSessionService.upvoteQuestion(questionId, user.id);
    } catch (err) {
      setError('Failed to upvote question. Please try again.');
      console.error('Error upvoting question:', err);
    }
  };

  const deleteQuestion = async (questionId: string) => {
    if (!user) return;

    try {
      await LiveSessionService.deleteQuestion(questionId, user.id);
    } catch (err) {
      setError('Failed to delete question. Please try again.');
      console.error('Error deleting question:', err);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return {
    questions,
    availableSessions,
    isSubmitting,
    error,
    submitQuestion,
    upvoteQuestion,
    deleteQuestion,
    clearError,
  };
}; 